import React, { useEffect, useState } from "react";

import { Header } from "./Header";
import { UserInfo } from "./UserInfo";
import { UserCompanyTable } from "./UserCompanyTable";
import { MainLayout } from "features/common/components/MainLayout/MainLayout";
import { Container, Wrapper } from "./styles";
import { useQuery } from "react-query";
import { deleteData, getData, put, post } from "services/api";
import { useHistory, useParams, useLocation } from "react-router-dom";
import { queryClient } from "index";
import { RootState } from "redux/reducers";
import { useDispatch, useSelector } from "react-redux";
import { setCurrentCompany, setDXUserData } from "redux/actions";
import { ModalDeleteCompany as ModalDeleteUser } from "features/Settings/ManageOrganizations/ModalDeleteCompany";
import toast from "react-hot-toast";
import { AccountContainer, FilterContainer, Text } from "features/Settings/ManageCompanies/ManageCompanies.styles";
import DXInputField from "features/common/components/DXInputField";
import ModalAddToWorkspace from "./ModalAddToWorkspace/ModalAddToWorkspace";

export interface IEditUser {
  id: string;
  cognito_id: string | null;
  email: string;
  created_at: string;
  updated_at: string | null;
  deleted_at: string | null;
  name: string;
  companies: {
    name: string;
    role: string;
    company_x_user_id: string;
    company_id: string;
  }[];
  org_role?: string;
}

export const EditUser = () => {
  interface LocationState {
    orgRole?: string
  }
  const history = useHistory();
  const { data: userData, isLoading } = useQuery(`getUser`, () =>
    getData(`/memberships/user/${user_id}`)
  );
  const { currentCompany } = useSelector((state: RootState) => state);
  const [user, setUser] = useState<IEditUser>(userData);
  const [companyXUserId, setCompanyXUserId] = useState();
  const location = useLocation<LocationState>();
  const [orgRole, setOrgRole] = useState<string | undefined>();
  const dispatch = useDispatch();

  const [isOpenRemoveUser, setIsOpenRemoveUser] = useState<boolean>(false);
  const [isOpenRemoveUserAllAccounts, setIsOpenRemoveUserAllAcounts] =
    useState<boolean>(false);
  const [isOpenRemoveUserFromOrg, setIsOpenRemoveUserFromOrg] = useState<boolean>(false);
  const [filterValue, setFilterValue] = useState<string>("");
  const [isOpenAddWorkspace, setIsOpenAddWorkspace] = useState<boolean>(false);
  const { user_id } = useParams<{ user_id: string }>();

  const handleInputChange = (fieldId, value, index?: number) => {
    const newUser = { ...user };
    if (index !== undefined) newUser.companies[index][fieldId] = value;
    else newUser[fieldId] = value;
    setUser(newUser);
    if (newUser.org_role) setOrgRole(newUser.org_role);
  };

  useEffect(() => {
    if (userData) {
      setUser(userData);
    }
  }, [userData]);

  useEffect(() => {
    if (location?.state?.orgRole) {
      setOrgRole(location.state.orgRole);
    }
  }, [location])

  const handleDeleteUser = (user_id) => {
    deleteData(`memberships/user/${user_id}`)
      .then(() => {
        setIsOpenRemoveUserAllAcounts(false);
        history.push({
          pathname: "/settings",
          state: {
            tabValue: 2,
          },
        });
      })
      .catch((error) => {
        console.error("Error deleting user:", error);
      });
  };

  const handleDeleteUserFromOrg = async (userId) => {
    deleteData(`memberships/user/${userId}`)
      .then(async () => {
        setIsOpenRemoveUserFromOrg(false);
        queryClient.invalidateQueries("getUsers");
        const resp = await getData(`/memberships/user/profile`);
        const rbac = resp.rbac;
        const account = resp.accounts.find((acc) => acc?.id === currentCompany?.account?.id);
        const company = account.companies.find((comp) => comp?.id === currentCompany?.company?.id);
        dispatch(setDXUserData(resp));
        dispatch(setCurrentCompany({ account, company, rbac }))
        history.push({
          pathname: "/settings",
          state: {
            tabValue: 2,
          },
        });
      })
      .catch((error) => {
        console.error("Error deleting user:", error);
        toast.error("Error deleting user");
      });
    toast.success("User deleted successfully");
  };

  const handleDeleteUserCompany = (companyXUserId) => {
    deleteData(`memberships/userXcompany/${companyXUserId}`)
      .then(() => {
        setIsOpenRemoveUser(false);
        queryClient.invalidateQueries(`getUser`);
      })
      .catch((error) => {
        console.error("Error deleting user:", error);
      });
  };

  const handleSubmitUser = async () => {
    try {
      await post(`/memberships/user`, {
        account_role: user.org_role ?? orgRole,
        account_id: currentCompany.account?.id,
        companies: [],
        email: user.email,
        name: user.name
      });

      for (const company of user.companies) {
        await put(`/memberships/userXcompany/${company.company_x_user_id}`, {
          role: company.role,
        });
      }

      await put(`/memberships/user/${userData?.id}`, {
        name: user.name,
      });

      history.push("/settings");
      toast.success("User updated successfully");
    } catch (error) {
      console.error("Error updating user:", error);
      toast.error("Error updating user");
    }
  };

  const handleFilterChange = (event) => {
    setFilterValue(event.target.value);
  };

  const filteredCompanies = user?.companies?.filter((company) =>
    company.name.toLowerCase().includes(filterValue.toLowerCase())
  );

  return (
    <>
      {orgRole && (
        <MainLayout>
          <Container>
            <Header onSubmit={handleSubmitUser} />
            <Wrapper>
              <UserInfo
                user={user}
                handleInputChange={handleInputChange}
                setIsOpenRemoveUserAllAcounts={setIsOpenRemoveUserAllAcounts}
              />
              <FilterContainer>
                <AccountContainer>
                  <Text className="filter">Filter Accounts:</Text>
                  <DXInputField
                    sx={{ paddingLeft: '10px' }}
                    placeholder="Search company name..."
                    onChange={(event) => handleFilterChange(event)}
                  />
                </AccountContainer>
              </FilterContainer>
              <UserCompanyTable
                companies={filteredCompanies}
                handleInputChange={handleInputChange}
                setIsOpenRemoveUser={setIsOpenRemoveUser}
                setIsOpenRemoveUserFromOrg={setIsOpenRemoveUserFromOrg}
                setIsOpenAddWorkspace={setIsOpenAddWorkspace}
                setCompanyXUserId={setCompanyXUserId}
                organization={currentCompany?.account?.name}
                orgRole={orgRole}
              />
            </Wrapper>
          </Container>
        </MainLayout>
      )}
      {isOpenRemoveUser && (
        <ModalDeleteUser
          isOpenModalDelete={isOpenRemoveUser}
          setIsOpenModalDelete={setIsOpenRemoveUser}
          text={`Are you sure you want to remove ${user?.name} from this account?`}
          confirmLabel="Remove User"
          handleDelete={() => handleDeleteUserCompany(companyXUserId)}
          title="Remove User"
        />
      )}
      {isOpenAddWorkspace && (
        <ModalAddToWorkspace
          isOpen={isOpenAddWorkspace}
          closeModal={() => setIsOpenAddWorkspace(false)}
          className="addUser"
          companies={currentCompany?.account?.companies}
          accountId={currentCompany?.account?.id}
          userLeft={20}
          editedUser={user}
        />
      )}
      {isOpenRemoveUserAllAccounts && (
        <ModalDeleteUser
          isOpenModalDelete={isOpenRemoveUserAllAccounts}
          setIsOpenModalDelete={setIsOpenRemoveUserAllAcounts}
          text={`Would you like to delete ${user?.name} from all accounts?`}
          confirmLabel="Delete User"
          handleDelete={() => handleDeleteUser(user?.id)}
          title="Delete User"
        />
      )}
      {isOpenRemoveUserFromOrg && (
        <ModalDeleteUser
          isOpenModalDelete={isOpenRemoveUserFromOrg}
          setIsOpenModalDelete={setIsOpenRemoveUserFromOrg}
          text={`Are you sure you want to remove ${user?.name} from this organization?`}
          confirmLabel="Remove User"
          handleDelete={() => handleDeleteUserFromOrg(user?.id)}
          title="Remove User"
        />
      )}
    </>
  );
};
